import * as Popover from '@radix-ui/react-popover';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useState, type MouseEvent } from 'react';
import { useOptionalObjectScope } from '@/shared/object-scope';
import { BookingBarFace, bookingBarClasses, bookingSlotClasses } from './BookingBarFace';
import { BookingDetails } from './BookingDetails';
import { bookingDetailsModel, type GuestAccess } from './bookingDetailsModel';
import { BookingDetailsSheet } from './BookingDetailsSheet';
import { barAria } from './calendarBarText';
import { classifyChannel, type BookingSpan } from './calendarHelpers';
import { computePillLayout } from './pillLayout';

interface Props {
  span: BookingSpan;
  propertyId: string;
  propertyName: string;
  gridStart: number;
  currency: string;
  dimmed: boolean;
  /** Gastdaten-Recht: Name und Kontakte; ohne es „Buchung {Nummer}“. */
  guestAccess: GuestAccess;
  /** Betrieb: Plan im Balken und Onboarding-Link. */
  operations: boolean;
  today: string;
  canReassign?: boolean;
  onReassignRequested?: () => void;
  /** Bahnen der Zeile, wenn sich eigene Buchungen überschneiden (bis zu drei). */
  lanes?: 1 | 2 | 3;
  lane?: 0 | 1 | 2;
  /** Handy: niedriger, ohne Kanalzeichen. */
  compact?: boolean;
  labels?: 'full' | 'short' | 'none';
}

/**
 * Ein Aufenthalt als Balken über den Nächten (Board „Belegung“): Kanalzeichen, „Gast · 2 Erw.“,
 * Schräge nur am Wechseltag, gerade Kante am Rand des Fensters. Klick öffnet die Details, am Handy
 * als Blatt; Ziehen auf eine andere Zeile schlägt einen Apartmentwechsel vor.
 */
export function BookingPill({
  span,
  propertyId,
  propertyName,
  gridStart,
  currency,
  dimmed,
  guestAccess,
  operations,
  today,
  canReassign = false,
  onReassignRequested,
  lanes = 1,
  lane = 0,
  compact = false,
  labels = 'full',
}: Props) {
  const channel = classifyChannel(span.channel);
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const layout = computePillLayout(span, gridStart);
  const drag = useDraggable({
    id: `booking-${span.bookingId}`,
    disabled: !canReassign,
    data: { bookingId: span.bookingId, sourcePropertyId: propertyId, span },
  });
  const group = useOptionalObjectScope()?.group;
  const model = bookingDetailsModel(span, {
    guestAccess,
    propertyName,
    currency,
    checkInTime: group?.checkInTime,
    checkOutTime: group?.checkOutTime,
    today,
  });
  const details = {
    span,
    model,
    propertyId,
    canOpenOnboarding: operations,
    canReassign,
    onReassignRequested,
  };

  // Die Details öffnen erst per Klick: beim Überfahren vieler Buchungen sprangen sonst ständig
  // Dialoge auf. Am Telefon kommt dasselbe als Blatt.
  const activate = (event: MouseEvent) => {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    event.preventDefault();
    setSheetOpen(true);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        ref={drag.setNodeRef}
        className={bookingSlotClasses({ lanes, lane, compact, dimmed, dragging: drag.isDragging })}
        style={{
          gridColumn: layout.gridColumn,
          gridRow: 1,
          transform: CSS.Translate.toString(drag.transform),
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            {...drag.attributes}
            {...drag.listeners}
            onClick={activate}
            aria-expanded={open}
            aria-label={barAria(span, guestAccess.contact)}
            className={bookingBarClasses(span, channel, { lanes, compact, open })}
            style={layout.clipPath ? { clipPath: layout.clipPath } : undefined}
          >
            <BookingBarFace
              span={span}
              channel={channel}
              layout={layout}
              laned={lanes > 1}
              compact={compact}
              labels={labels}
              canSeeGuests={guestAccess.contact}
              operations={operations}
            />
          </button>
        </Popover.Trigger>
      </div>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          // Nie höher als der Platz bis zum Fensterrand — sonst ragte er oben oder unten hinaus.
          className="bg-card border-hairline shadow-panel rounded-tile z-50 hidden max-h-[var(--radix-popover-content-available-height)] w-[min(360px,calc(100vw-32px))] overflow-y-auto overscroll-contain border md:block"
        >
          <BookingDetails {...details} />
        </Popover.Content>
      </Popover.Portal>

      <BookingDetailsSheet open={sheetOpen} onOpenChange={setSheetOpen} {...details} />
    </Popover.Root>
  );
}

export { computePillLayout } from './pillLayout';
